import React, { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import { Plus, Save, Trash2 } from "lucide-react";
import { CloseIcon, CoffeeIcon, SpinnerIcon } from "../../Icons";
import DateTimePicker from "./DateTimePicker";
import {
  addAttendanceByAdmin,
  updateAttendance,
} from "../../services/attendanceService";
import {
  DASHBOARD_BTN,
  DASHBOARD_FIELD,
  DASHBOARD_MODAL,
} from "./dashboardTheme";
import {
  attendanceDatePart,
  toAttendancePayloadDateTime,
  utcToAttendanceLocalDateTime,
  withLockedAttendanceDate,
} from "../../utils/dateUtils";

function newBreakRow(lockedDate, seedTime = "12:00") {
  const date = lockedDate || attendanceDatePart(seedTime) || "";
  const time = seedTime.includes("T") ? seedTime.split("T")[1].slice(0, 5) : seedTime;
  const start = date ? `${date}T${time}` : "";
  return {
    key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    id: null,
    breakInTime: start,
    breakOutTime: start,
  };
}

function toMs(localDateTime) {
  if (!localDateTime) return 0;
  const m = String(localDateTime).match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{1,2}):(\d{2})/,
  );
  if (!m) return 0;
  return Date.UTC(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4]),
    Number(m[5]),
    0,
  );
}

function validateForm(formData, isAddMode, userId, lockedDate) {
  if (isAddMode && !userId) return "User is required to add attendance.";
  if (!formData.clockInTime?.trim()) return "Clock In time is required.";
  if (!formData.clockOutTime?.trim()) return "Clock Out time is required.";

  if (lockedDate) {
    if (attendanceDatePart(formData.clockInTime) !== lockedDate) {
      return "Clock In date cannot be changed. Only the time of day can be updated.";
    }
    if (attendanceDatePart(formData.clockOutTime) !== lockedDate) {
      return "Clock Out date cannot be changed. Only the time of day can be updated.";
    }
  }

  const clockIn = toMs(formData.clockInTime);
  const clockOut = toMs(formData.clockOutTime);
  if (clockOut <= clockIn) {
    return "Clock Out must be after Clock In.";
  }

  const filledBreaks = (formData.breaks || []).filter(
    (b) => b.breakInTime?.trim() || b.breakOutTime?.trim(),
  );

  const parsed = [];
  for (let i = 0; i < filledBreaks.length; i++) {
    const br = filledBreaks[i];
    const n = i + 1;
    if (!br.breakInTime?.trim() || !br.breakOutTime?.trim()) {
      return `Break ${n}: provide both start and end, or remove the row.`;
    }
    if (lockedDate) {
      if (attendanceDatePart(br.breakInTime) !== lockedDate) {
        return `Break ${n} start date cannot be changed.`;
      }
      if (attendanceDatePart(br.breakOutTime) !== lockedDate) {
        return `Break ${n} end date cannot be changed.`;
      }
    }
    const start = toMs(br.breakInTime);
    const end = toMs(br.breakOutTime);
    if (end <= start) {
      return `Break ${n}: end must be after start.`;
    }
    if (start < clockIn || end > clockOut) {
      return `Break ${n} must be within Clock In and Clock Out.`;
    }
    parsed.push({ n, start, end });
  }

  const sorted = [...parsed].sort((a, b) => a.start - b.start);
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].start < sorted[i - 1].end) {
      return `Break ${sorted[i].n} overlaps Break ${sorted[i - 1].n}.`;
    }
  }

  return null;
}

const EditAttendanceModal = ({
  isOpen,
  onClose,
  attendance,
  onSuccess,
  userId,
}) => {
  const [formData, setFormData] = useState({
    clockInTime: "",
    clockOutTime: "",
    breaks: [],
  });
  const [loading, setLoading] = useState(false);

  const isAddMode = attendance && !attendance.attendanceId && attendance.date;

  const lockedDate = useMemo(() => {
    if (!attendance) return "";
    if (isAddMode && attendance.date) {
      return String(attendance.date).slice(0, 10);
    }
    const fromClock = utcToAttendanceLocalDateTime(attendance.clockInTime);
    if (fromClock) return attendanceDatePart(fromClock);
    if (attendance.date) return String(attendance.date).slice(0, 10);
    return "";
  }, [attendance, isAddMode]);

  useEffect(() => {
    if (attendance && isOpen) {
      if (isAddMode) {
        const datePart = lockedDate || new Date().toLocaleDateString("en-CA", {
          timeZone: "America/Los_Angeles",
        });
        setFormData({
          clockInTime: `${datePart}T09:00`,
          clockOutTime: `${datePart}T17:00`,
          breaks: [],
        });
      } else {
        const clockInTime = utcToAttendanceLocalDateTime(attendance.clockInTime);
        const clockOutTime = utcToAttendanceLocalDateTime(
          attendance.clockOutTime,
        );
        const datePart =
          lockedDate ||
          attendanceDatePart(clockInTime) ||
          String(attendance.date || "").slice(0, 10);
        const rawBreaks = Array.isArray(attendance.breaks)
          ? [...attendance.breaks]
          : [];
        rawBreaks.sort((a, b) => {
          const ta = new Date(a.breakInTime || 0).getTime();
          const tb = new Date(b.breakInTime || 0).getTime();
          return ta - tb;
        });
        setFormData({
          clockInTime: withLockedAttendanceDate(clockInTime, datePart),
          clockOutTime: withLockedAttendanceDate(clockOutTime, datePart),
          breaks: rawBreaks.map((brk, idx) => ({
            key: `brk-${brk.id ?? idx}`,
            id: brk.id ?? null,
            breakInTime: withLockedAttendanceDate(
              utcToAttendanceLocalDateTime(brk.breakInTime),
              datePart,
            ),
            breakOutTime: withLockedAttendanceDate(
              utcToAttendanceLocalDateTime(brk.breakOutTime),
              datePart,
            ),
          })),
        });
      }
    } else {
      setFormData({
        clockInTime: "",
        clockOutTime: "",
        breaks: [],
      });
    }
  }, [attendance, isOpen, isAddMode, lockedDate]);

  if (!isOpen) return null;

  const setClockField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: lockedDate ? withLockedAttendanceDate(value, lockedDate) : value,
    }));
  };

  const setBreakField = (key, field, value) => {
    const nextVal = lockedDate
      ? withLockedAttendanceDate(value, lockedDate)
      : value;
    setFormData((prev) => ({
      ...prev,
      breaks: prev.breaks.map((b) =>
        b.key === key ? { ...b, [field]: nextVal } : b,
      ),
    }));
  };

  const addBreak = () => {
    setFormData((prev) => {
      const seed =
        prev.clockInTime ||
        (lockedDate ? `${lockedDate}T12:00` : "");
      return {
        ...prev,
        breaks: [...prev.breaks, newBreakRow(lockedDate, seed)],
      };
    });
  };

  const removeBreak = (key) => {
    setFormData((prev) => ({
      ...prev,
      breaks: prev.breaks.filter((b) => b.key !== key),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm(
      formData,
      isAddMode,
      userId,
      lockedDate,
    );
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setLoading(true);
    try {
      const clockInTime = toAttendancePayloadDateTime(formData.clockInTime);
      const clockOutTime = toAttendancePayloadDateTime(formData.clockOutTime);
      const breaksPayload = (formData.breaks || [])
        .filter((b) => b.breakInTime?.trim() && b.breakOutTime?.trim())
        .map((b) => ({
          ...(b.id ? { id: b.id } : {}),
          breakInTime: toAttendancePayloadDateTime(b.breakInTime),
          breakOutTime: toAttendancePayloadDateTime(b.breakOutTime),
        }));

      if (isAddMode) {
        const response = await addAttendanceByAdmin({
          userId,
          clockInTime,
          clockOutTime,
          breaks: breaksPayload,
        });
        const ok = response.success === true || response.status === 1;
        if (ok) {
          toast.success(response.message || "Attendance added successfully");
          onSuccess?.();
          onClose();
        } else {
          toast.error(response.message || "Failed to add attendance");
        }
      } else {
        const attendanceId = attendance?.attendanceId;
        if (!attendanceId) {
          toast.error(
            "Unable to identify attendance record. Please refresh and try again.",
          );
          setLoading(false);
          return;
        }
        const response = await updateAttendance(attendanceId, {
          clockInTime,
          clockOutTime,
          breaks: breaksPayload,
        });
        const ok = response.success === true || response.status === 1;
        if (ok) {
          toast.success(
            response.message || "Attendance updated successfully",
          );
          onSuccess?.();
          onClose();
        } else {
          toast.error(response.message || "Failed to update attendance");
        }
      }
    } catch (error) {
      console.error(
        isAddMode ? "Add attendance failed" : "Update attendance failed",
        error,
      );
      const message =
        error?.data?.message ||
        error?.message ||
        (isAddMode ? "Failed to add attendance" : "Failed to update attendance");
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={DASHBOARD_MODAL.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="attendance-modal-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onClose?.();
      }}
    >
      <div className={DASHBOARD_MODAL.panelWide}>
        <div className={DASHBOARD_MODAL.head}>
          <div className="min-w-0">
            <h3 id="attendance-modal-title" className={DASHBOARD_MODAL.title}>
              {isAddMode ? "Add Attendance" : "Edit Attendance"}
            </h3>
            <p className={DASHBOARD_MODAL.subtitle}>
              {isAddMode
                ? "Create a clock-in / clock-out record. Date is fixed to the selected day."
                : "Update clock-in, clock-out, and every break. Date cannot be changed."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className={DASHBOARD_MODAL.close}
            aria-label="Close"
          >
            <CloseIcon size={14} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className={`${DASHBOARD_MODAL.body} space-y-4`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={DASHBOARD_FIELD.label}>Clock In</label>
                <DateTimePicker
                  value={formData.clockInTime}
                  onChange={(value) => setClockField("clockInTime", value)}
                  placeholder="Select time"
                  lockDate={lockedDate}
                />
              </div>
              <div>
                <label className={DASHBOARD_FIELD.label}>Clock Out</label>
                <DateTimePicker
                  value={formData.clockOutTime}
                  onChange={(value) => setClockField("clockOutTime", value)}
                  placeholder="Select time"
                  lockDate={lockedDate}
                />
              </div>
            </div>

            <div className="rounded-lg border border-amber-100 bg-amber-50/40">
              <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-amber-100">
                <div className="flex items-center gap-1.5 min-w-0">
                  <CoffeeIcon className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                  <p className="text-[11px] font-semibold text-amber-900">
                    Breaks
                  </p>
                  <span className="text-[11px] text-amber-700/80">
                    {formData.breaks.length === 0
                      ? "None"
                      : `${formData.breaks.length} recorded`}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={addBreak}
                  disabled={loading}
                  className="inline-flex items-center gap-1 h-7 px-2 text-[11px] font-medium rounded-md border border-amber-200 bg-white text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add break
                </button>
              </div>

              {formData.breaks.length === 0 ? (
                <p className="px-3 py-3 text-[12px] text-slate-500">
                  No breaks on this session. Save without breaks, or add one.
                </p>
              ) : (
                <div className="p-3 space-y-3">
                  {formData.breaks.map((brk, idx) => (
                    <div
                      key={brk.key}
                      className="rounded-md border border-amber-100 bg-white p-3"
                    >
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <p className="text-[11px] font-medium text-slate-700">
                          Break {idx + 1}
                        </p>
                        <button
                          type="button"
                          onClick={() => removeBreak(brk.key)}
                          disabled={loading}
                          className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-slate-200 text-slate-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200 disabled:opacity-50"
                          aria-label={`Remove break ${idx + 1}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className={DASHBOARD_FIELD.label}>
                            Break start
                          </label>
                          <DateTimePicker
                            value={brk.breakInTime}
                            onChange={(value) =>
                              setBreakField(brk.key, "breakInTime", value)
                            }
                            placeholder="Select time"
                            lockDate={lockedDate}
                          />
                        </div>
                        <div>
                          <label className={DASHBOARD_FIELD.label}>
                            Break end
                          </label>
                          <DateTimePicker
                            value={brk.breakOutTime}
                            onChange={(value) =>
                              setBreakField(brk.key, "breakOutTime", value)
                            }
                            placeholder="Select time"
                            lockDate={lockedDate}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={DASHBOARD_MODAL.footer}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className={DASHBOARD_BTN.modalCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={DASHBOARD_BTN.modalPrimary}
            >
              {loading ? (
                <SpinnerIcon className="animate-spin" size={14} />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {isAddMode ? "Add Record" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAttendanceModal;
